import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { getUploadHistory } from "./api"
import { PAGE_SIZE_OPTIONS } from "@ciq/shared/components/paginatedTable"
import { SHORT_POLL_INTERVAL } from "@ciq/shared/api/api"
import { formatTimestamp } from "@ciq/shared/utils/format"

const HISTORY_DEFAULT_PAGE_SIZE = 10

/**
 * Statuses that indicate a job is still in progress and the history should keep polling.
 */
const IN_PROGRESS_STATUSES = ["Rolling Back"]

/**
 * Validate and normalize a page size value.
 * Returns the value if valid, or HISTORY_DEFAULT_PAGE_SIZE if invalid.
 * @param {number} size - Page size to validate
 * @returns {number} Valid page size
 */
function validatePageSize(size) {
  if (PAGE_SIZE_OPTIONS.includes(size)) {
    return size
  }
  return HISTORY_DEFAULT_PAGE_SIZE
}

/**
 * Filter jobs by search query (multi-field: uploaded_by, display status, date).
 * @param {Array} jobs - All jobs
 * @param {string} query - Search query
 * @returns {Array} Filtered jobs
 */
function filterJobs(jobs, query) {
  if (!query) return jobs
  const q = query.toLowerCase()
  return jobs.filter(j =>
    j.uploaded_by?.toLowerCase().includes(q) ||
    (j.status || "").toLowerCase().includes(q) ||
    (formatTimestamp(j.date) || "").toLowerCase().includes(q)
  )
}

/**
 * @typedef {import("./api").HistoryEntry} HistoryEntry
 */

/**
 * @typedef {Object} UploadHistoryDataResult
 * @property {Array<HistoryEntry>} data - Current page of jobs (filtered + paginated)
 * @property {Array<HistoryEntry>} allJobs - Complete unfiltered job array
 * @property {number} totalCount - Total jobs (filtered count if searching)
 * @property {number} page - Current page (0-indexed)
 * @property {number} pageSize - Current page size
 * @property {string} searchQuery - Current search query
 * @property {boolean} isLoaded - Boolean indicating if the initial fetch has completed
 * @property {string|null} error - Error message if failed
 * @property {function(number): void} setPage - Change page
 * @property {function(number): void} setPageSize - Change page size (resets to page 0)
 * @property {function(string): void} setSearchQuery - Update search (resets to page 0)
 * @property {function(): void} refetch - Re-fetch history data from the API (preserves pagination/search state)
 * @property {boolean} hasInProgressJobs - Boolean indicating if any job has a non-terminal status (triggers auto-polling)
 */

/**
 * Hook for fetching and managing upload history data with client-side pagination and search.
 *
 * @param {string} schoolId - School identifier (null to skip fetch)
 * @returns {UploadHistoryDataResult}
 */
export function useUploadHistoryData(schoolId) {
  const [allJobs, setAllJobs] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(HISTORY_DEFAULT_PAGE_SIZE)
  const [searchQuery, setSearchQuery] = useState("")
  const [fetchToken, setFetchToken] = useState(0)

  const refetch = useCallback(() => {
    setFetchToken(t => t + 1)
  }, [])

  // Track previous schoolId to only reset on school change, not on refetch
  const prevSchoolIdRef = useRef(schoolId)

  useEffect(() => {
    if (!schoolId) {
      setAllJobs([])
      setIsLoaded(true)
      setError(null)
      prevSchoolIdRef.current = schoolId
      return
    }

    // Only clear data when switching schools — not on refetch (avoids table flash during polling)
    if (prevSchoolIdRef.current !== schoolId) {
      setAllJobs([])
      setIsLoaded(false)
      prevSchoolIdRef.current = schoolId
    }

    const controller = new AbortController()
    let cancelled = false

    async function fetchHistory() {
      setError(null)

      try {
        const data = await getUploadHistory(schoolId, controller.signal)
        if (cancelled) return
        setAllJobs(data.jobs)
        setIsLoaded(true)
      } catch (err) {
        if (cancelled) return
        if (err.name === "AbortError" || err.name === "CanceledError") return
        console.error("Failed to load upload history:", err)
        setError("Failed to load upload history")
        setIsLoaded(true)
      }
    }

    fetchHistory()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [schoolId, fetchToken])

  // Auto-poll when any job has a non-terminal status (e.g., "Rolling Back", "Applying")
  const hasInProgressJobs = useMemo(
    () => allJobs.some(j => IN_PROGRESS_STATUSES.includes(j.status)),
    [allJobs]
  )

  useEffect(() => {
    if (!hasInProgressJobs) return

    const intervalId = setInterval(() => {
      setFetchToken(t => t + 1)
    }, SHORT_POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [hasInProgressJobs])

  // Reset search/pagination when schoolId changes
  useEffect(() => {
    setSearchQuery("")
    setPage(0)
  }, [schoolId])

  const handleSetSearchQuery = useCallback((query) => {
    setSearchQuery(query || "")
    setPage(0)
  }, [])

  const handleSetPageSize = useCallback((newSize) => {
    setPageSize(validatePageSize(newSize))
    setPage(0)
  }, [])

  // Compute filtered and paginated data
  const { data, totalCount } = useMemo(() => {
    const filtered = filterJobs(allJobs, searchQuery)
    const start = page * pageSize
    return {
      data: filtered.slice(start, start + pageSize),
      totalCount: filtered.length
    }
  }, [allJobs, searchQuery, page, pageSize])

  return {
    data,
    allJobs,
    totalCount,
    page,
    pageSize,
    searchQuery,
    isLoaded,
    error,
    setPage,
    setPageSize: handleSetPageSize,
    setSearchQuery: handleSetSearchQuery,
    refetch,
    hasInProgressJobs
  }
}
